/**
 * 카테고리 연동 기간 입력 (시작일 + 기간 select + 자동 만료일) 공용 유틸.
 *
 * - CategoryAddModal: 카테고리 추가 시 기간 입력
 * - CategoryContent: [1] 연동 기간 박스 (기존 카테고리 수정)
 *
 * 둘 다 동일한 UX 패턴을 사용하므로 헬퍼·타입을 한 곳에 모아둔다.
 */

/** 기간 select 옵션 값 — N년 또는 "직접 선택" */
export type CategoryPeriodDuration =
  | "1Y"
  | "2Y"
  | "3Y"
  | "4Y"
  | "5Y"
  | "CUSTOM";

/** select 표시용 옵션 정의 (default 는 "1Y") */
export const CATEGORY_PERIOD_DURATION_OPTIONS: Array<{
  value: CategoryPeriodDuration;
  label: string;
}> = [
  { value: "1Y", label: "1년" },
  { value: "2Y", label: "2년" },
  { value: "3Y", label: "3년" },
  { value: "4Y", label: "4년" },
  { value: "5Y", label: "5년" },
  { value: "CUSTOM", label: "직접 선택" },
];

/** N년 옵션을 숫자(year offset) 로 매핑. CUSTOM 은 null. */
export const durationToYears = (
  duration: CategoryPeriodDuration,
): number | null => {
  switch (duration) {
    case "1Y":
      return 1;
    case "2Y":
      return 2;
    case "3Y":
      return 3;
    case "4Y":
      return 4;
    case "5Y":
      return 5;
    case "CUSTOM":
      return null;
  }
};

/**
 * `yyyy-MM-dd` 문자열에 N년을 더해 같은 형식의 문자열로 리턴.
 *
 * - 빈 문자열이면 `""` 그대로 리턴 (자동 계산 안 함)
 * - `Date` 로 파싱 후 `setFullYear(+years)` — 윤년/2월29일도 JS 기본 동작 따름
 */
export const addYears = (dateStr: string, years: number): string => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  d.setFullYear(d.getFullYear() + years);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/**
 * 기존 startDate / endDate 차이로부터 select 의 기본 옵션을 추정.
 *
 * 규칙:
 * - 둘 중 하나라도 비어 있으면 `"CUSTOM"`
 * - 연 차이가 1/2/3/5 중 하나이고 월·일이 동일하면 해당 옵션
 * - 그 외 모두 `"CUSTOM"`
 *
 * 시작일 + N년 == 만료일 인 "정확히 떨어지는 N년" 케이스만 select 매칭하고,
 * 나머지는 사용자가 임의 입력한 것으로 보고 직접 선택을 띄운다.
 */
export const inferDurationFromDates = (
  startDate: string,
  endDate: string,
): CategoryPeriodDuration => {
  if (!startDate || !endDate) return "CUSTOM";
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return "CUSTOM";
  }
  const sameMonthDay =
    start.getMonth() === end.getMonth() && start.getDate() === end.getDate();
  if (!sameMonthDay) return "CUSTOM";
  const diff = end.getFullYear() - start.getFullYear();
  switch (diff) {
    case 1:
      return "1Y";
    case 2:
      return "2Y";
    case 3:
      return "3Y";
    case 4:
      return "4Y";
    case 5:
      return "5Y";
    default:
      return "CUSTOM";
  }
};